import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogOut, Plus, Edit, Trash2, Calendar, RefreshCw, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MatchAdmin = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [formData, setFormData] = useState({
    team_id: '',
    opponent: '',
    date: '',
    time: '',
    location: '',
    is_home: true,
    competition: '',
    result: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('tennis_token');
    const memberData = localStorage.getItem('tennis_member');
    
    if (!token || !memberData) {
      navigate('/tennis/login');
      return;
    }

    const parsedMember = JSON.parse(memberData);
    
    if (!parsedMember.is_admin) {
      toast.error('Du har ikke admin-tilgang');
      navigate('/tennis/booking');
      return;
    }

    setMember(parsedMember);
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const [teamsRes, matchesRes] = await Promise.all([
        fetch(`${API_URL}/api/matches/teams`),
        fetch(`${API_URL}/api/matches/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (matchesRes.ok) setMatches(await matchesRes.json());
    } catch (error) {
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('tennis_token');
    
    try {
      const url = editingMatch 
        ? `${API_URL}/api/matches/${editingMatch.id}`
        : `${API_URL}/api/matches`;
      
      const method = editingMatch ? 'PUT' : 'POST';
      
      const body = editingMatch 
        ? { 
            opponent: formData.opponent,
            date: formData.date,
            time: formData.time,
            location: formData.location,
            is_home: formData.is_home,
            competition: formData.competition,
            result: formData.result || null
          }
        : formData;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(editingMatch ? 'Kamp oppdatert!' : 'Kamp lagt til!');
        setShowForm(false);
        setEditingMatch(null);
        resetForm();
        fetchData(token);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Noe gikk galt');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    }
  };

  const handleEdit = (match) => {
    setEditingMatch(match);
    setFormData({
      team_id: match.team_id,
      opponent: match.opponent,
      date: match.date,
      time: match.time,
      location: match.location,
      is_home: match.is_home,
      competition: match.competition || '',
      result: match.result || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (matchId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne kampen?')) return;
    
    const token = localStorage.getItem('tennis_token');
    
    try {
      const response = await fetch(`${API_URL}/api/matches/${matchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Kamp slettet');
        fetchData(token);
      } else {
        toast.error('Kunne ikke slette kamp');
      }
    } catch (error) {
      toast.error('Nettverksfeil');
    }
  };

  const resetForm = () => {
    setFormData({
      team_id: '',
      opponent: '',
      date: '',
      time: '',
      location: '',
      is_home: true,
      competition: '',
      result: ''
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('tennis_token');
    localStorage.removeItem('tennis_member');
    navigate('/tennis');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-900">Laster...</div>
      </div>
    );
  }

  return (
    <div data-testid="match-admin-page" className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-500 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Kampkalender Admin</h1>
              <p className="text-slate-500">Administrer fotballkamper</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => {
                setEditingMatch(null);
                resetForm();
                setShowForm(true);
              }}
              className="bg-green-500 text-white font-bold hover:bg-green-500/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Legg til kamp
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(localStorage.getItem('tennis_token'))}
              className="border-white/20 text-slate-900 hover:bg-slate-100"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Oppdater
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-white/20 text-slate-900 hover:bg-slate-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logg ut
            </Button>
          </div>
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl border border-white/10 p-6 w-full max-w-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-slate-900">
                  {editingMatch ? 'Rediger kamp' : 'Legg til ny kamp'}
                </h2>
                <button 
                  onClick={() => {
                    setShowForm(false);
                    setEditingMatch(null);
                    resetForm();
                  }}
                  className="text-slate-500 hover:text-slate-900"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Lag *</label>
                  <select
                    value={formData.team_id}
                    onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                    className="w-full bg-slate-100 border border-white/10 text-slate-900 rounded-md px-3 py-2"
                    required
                    disabled={!!editingMatch}
                  >
                    <option value="">Velg lag</option>
                    <optgroup label="Voksen">
                      {teams.filter(t => t.category === 'voksen').map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Ungdom">
                      {teams.filter(t => t.category === 'ungdom').map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Motstander *</label>
                  <Input
                    type="text"
                    value={formData.opponent}
                    onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
                    className="bg-slate-100 border-white/10 text-slate-900"
                    placeholder="F.eks. Grorud IL"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">Dato *</label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="bg-slate-100 border-white/10 text-slate-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">Klokkeslett *</label>
                    <Input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="bg-slate-100 border-white/10 text-slate-900"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Hjemme/Borte *</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_home"
                        checked={formData.is_home}
                        onChange={() => setFormData({ ...formData, is_home: true })}
                        className="text-green-500"
                      />
                      <span className="text-slate-900">Hjemmekamp</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_home"
                        checked={!formData.is_home}
                        onChange={() => setFormData({ ...formData, is_home: false })}
                        className="text-green-500"
                      />
                      <span className="text-slate-900">Bortekamp</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    {formData.is_home ? 'Bane/Sted' : 'Bortebane/Adresse'}
                  </label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="bg-slate-100 border-white/10 text-slate-900"
                    placeholder={formData.is_home ? 'Veitvet kunstgress' : 'Grorud kunstgress'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">Turnering/Serie</label>
                  <Input
                    type="text"
                    value={formData.competition}
                    onChange={(e) => setFormData({ ...formData, competition: e.target.value })}
                    className="bg-slate-100 border-white/10 text-slate-900"
                    placeholder="F.eks. 4. divisjon, Cup, Treningskamp"
                  />
                </div>

                {editingMatch && (
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">Resultat</label>
                    <Input
                      type="text"
                      value={formData.result}
                      onChange={(e) => setFormData({ ...formData, result: e.target.value })}
                      className="bg-slate-100 border-white/10 text-slate-900"
                      placeholder="F.eks. 3-1"
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingMatch(null);
                      resetForm();
                    }}
                    className="flex-1 border-white/20 text-slate-900 hover:bg-slate-100"
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-green-500 text-white font-bold hover:bg-green-500/90"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingMatch ? 'Lagre endringer' : 'Legg til kamp'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{matches.length}</p>
                <p className="text-slate-500 text-sm">Totalt kamper</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {matches.filter(m => new Date(m.date) >= new Date()).length}
                </p>
                <p className="text-slate-500 text-sm">Kommende</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {matches.filter(m => m.is_home).length}
                </p>
                <p className="text-slate-500 text-sm">Hjemmekamper</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {matches.filter(m => m.result).length}
                </p>
                <p className="text-slate-500 text-sm">Med resultat</p>
              </div>
            </div>
          </div>
        </div>

        {/* Matches Table */}
        <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h2 className="text-lg font-bold text-slate-900">Alle kamper</h2>
          </div>
          
          {matches.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Ingen kamper registrert ennå. Klikk "Legg til kamp" for å starte.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Dato</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Lag</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Motstander</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">H/B</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Tid</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Serie</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Resultat</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Handlinger</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((match) => (
                    <tr key={match.id} className="border-b border-white/5 hover:bg-slate-50">
                      <td className="py-3 px-4 text-slate-900">
                        {new Date(match.date).toLocaleDateString('no-NO', { 
                          day: 'numeric', 
                          month: 'short' 
                        })}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-600 rounded">
                          {match.team_name}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-900 font-medium">{match.opponent}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          match.is_home 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-zinc-700 text-slate-600'
                        }`}>
                          {match.is_home ? 'H' : 'B'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{match.time}</td>
                      <td className="py-3 px-4 text-slate-500">{match.competition || '-'}</td>
                      <td className="py-3 px-4">
                        {match.result ? (
                          <span className="text-slate-900 font-bold">{match.result}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(match)}
                            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(match.id)}
                            className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick link */}
        <div className="mt-8 text-center">
          <Link to="/kamper" className="text-green-500 hover:underline">
            Se offentlig kampkalender →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MatchAdmin;
